import { useFormikContext } from 'formik'

import type { ShipmentType } from '@b-stock/shipment-api-client'

import type { BidFormFormikValues } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import isCanadianTaxCurrency from '@/buyer-portal/helpers/canadianTaxCurrency'

import MaxTaxExposureDisplay from './MaxTaxExposureDisplay'
import useMaxTaxExposure from './useMaxTaxExposure'

/**
 * MaxTaxExposureDisplayContainer — bid-confirmation max-exposure
 * wiring (T081 / FR-008 of the Canadian-tax spec).
 *
 * Bridges the pure presentational `MaxTaxExposureDisplay` component
 * to its data source (`useMaxTaxExposure`, which calls the listing
 * service's GET /max-exposure endpoint per T075). Reads the buyer's
 * intended bid amount from the surrounding `BidFormProvider` formik
 * context — the bid-confirmation modal is always rendered as a
 * descendant of `BidFormProvider`, so the form value here is the same
 * one the buyer is about to commit.
 *
 * **Single-amount form mapping**: `BidFormFormikValues.amount` is a
 * dollar value (the buyer's typed bid). The listing service expects
 * cents and exposes both `formSingleBid` (required) and
 * `formProxyMaxBid` (optional). On the home-portal bid form these two
 * collapse to the same field — the buyer's typed amount IS both the
 * single bid they're about to place AND the proxy max they're
 * authorising. We pass the same value for both so server-side R7
 * Reading B (`max(formSingleBid, formProxyMaxBid, priorExistingMax)`)
 * still correctly surfaces a higher prior bid when one exists.
 *
 * Renders `null` when:
 *   - the listing currency is outside the Canadian tax path (CAD/USD
 *     per FP-3012) — there's no rate to look up, so we short-circuit
 *     before the GET /max-exposure fires
 *   - the buyer hasn't selected a delivery address (FR-008 precondition)
 *   - the form `amount` hasn't been entered yet
 *   - the listing-service exposure lookup degraded (hook returns
 *     `undefined`)
 */
const MaxTaxExposureDisplayContainer = ({
  listingId,
  buyerAddressId,
  currencyCode,
  buyNowPrice,
  buyerFeeCents,
  shippingCents,
  shipmentType,
}: {
  listingId: string
  buyerAddressId: string | undefined
  currencyCode: string
  // Buy Now has no bid-input field, so the Formik `amount` this container
  // otherwise reads is unrelated to the purchase price — fall back to the
  // listing's buyNow price (mirrors AutoPayMethodProvider's `bidAmount`
  // resolution for the same isBuyNow-vs-bid ambiguity).
  buyNowPrice?: number
  // FP-2498 — client-computed BUYER_FEE + SHIPPING taxable bases
  // sourced from `useCalculatedAdditionalCharges.bstockFee` and
  // `selectedQuote.totalPrice` respectively. Both in cents; both
  // optional (svc/listing defaults each to 0 when omitted).
  buyerFeeCents?: number
  shippingCents?: number
  // FP-2496 — drives the estimate refetch on delivery-method change.
  shipmentType?: ShipmentType
}) => {
  const formikContext = useFormikContext<BidFormFormikValues>()
  const amountDollars = formikContext.values.amount || buyNowPrice || 0
  const formAmountCents = Math.round(amountDollars * 100)
  const isTaxable = isCanadianTaxCurrency(currencyCode)

  const data = useMaxTaxExposure({
    isTaxable,
    listingId,
    formSingleBid: formAmountCents,
    buyerAddressId,
    formProxyMaxBid: formAmountCents,
    buyerFeeCents,
    shippingCents,
    shipmentType,
  })

  if (!isTaxable || !data) {
    return null
  }

  return (
    <MaxTaxExposureDisplay
      data={data}
      formProxyMaxBid={formAmountCents}
      currencyCode={currencyCode}
    />
  )
}

export default MaxTaxExposureDisplayContainer
